import { useEffect, useRef, useState } from 'react';
import useDebounced from '../../../hooks/useDebounced';
import InputIconByKey from '../Input/InputIconByKey';
import RequiredAsteriskTooltip from './RequiredAsteriskTooltip';
import { IconKey } from '../../../types/system/icons';
import SvgIcon from '../../SvgIcon/SvgIcon';

interface OptionItem {
  code?: string;
  id: number;
  name: string;
}

interface SimpleSearchSelectProps {
  label: string;
  name: string;
  placeholder?: string;
  options: OptionItem[];
  selectedValue: number | null;
  onSelect: (selected: OptionItem) => void;
  onSearch?: (searchTerm: string) => void;
  searchPlaceholder?: string;
  error?: string;
  iconKey?: IconKey;
  disabled?: boolean;
  required?: boolean;
  rightButton?: React.ReactNode;
  onReload?: () => void;
  /**
   * Sobrescribe la clave usada en el atributo `data-field` (para scroll
   * automático a errores) cuando difiere de `name`.
   */
  dataField?: string;
}

const SimpleSearchSelect: React.FC<SimpleSearchSelectProps> = ({
  label,
  name,
  placeholder = 'Seleccione una opción',
  options,
  selectedValue,
  onSelect,
  onSearch,
  searchPlaceholder = 'Buscar...',
  error,
  iconKey,
  disabled,
  required,
  rightButton,
  onReload,
  dataField,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounced(searchTerm, 300);
  const [filteredOptions, setFilteredOptions] = useState<OptionItem[]>(options);
  const [isOpen, setIsOpen] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const lastSearchTermRef = useRef('');

  const selectedOption =
    options.find((opt) => opt.id === selectedValue) || null;

  useEffect(() => {
    const trimmed = debouncedSearchTerm.trim();

    if (onSearch) {
      if (trimmed !== '' && trimmed !== lastSearchTermRef.current) {
        lastSearchTermRef.current = trimmed;
        onSearch(trimmed);
      }

      if (trimmed === '') {
        lastSearchTermRef.current = '';
        setFilteredOptions([]);
      }
    } else {
      if (trimmed === '') {
        setFilteredOptions(options);
      } else {
        setFilteredOptions(
          options.filter((option) =>
            option.name.toLowerCase().includes(trimmed.toLowerCase())
          )
        );
      }
    }
  }, [debouncedSearchTerm, options, onSearch]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const trimmed = debouncedSearchTerm.trim();

    if (!onSearch) {
      if (trimmed === '') {
        setFilteredOptions(options);
      } else {
        setFilteredOptions(
          options.filter((option) =>
            option.name.toLowerCase().includes(trimmed.toLowerCase())
          )
        );
      }
    } else {
      setFilteredOptions(options);
    }
  }, [options, debouncedSearchTerm, onSearch]);

  useEffect(() => {
    if (!isOpen || disabled) return;

    const timeout = window.setTimeout(() => {
      searchInputRef.current?.focus();
      searchInputRef.current?.select();
    }, 0);

    return () => window.clearTimeout(timeout);
  }, [isOpen, disabled]);

  return (
    <div className="mb-5" data-field={dataField ?? name}>
      {label && (
        <label
          className="
          mb-2.5 flex items-center gap-1
          text-sm font-medium
          text-black
          dark:text-white
        "
        >
          {label}
          {required && <RequiredAsteriskTooltip delay={400} />}
        </label>
      )}

      <div className="flex items-start">
        <div className="relative min-w-0 flex-1" ref={containerRef}>
          <div
            className={`
            bg-gray-1 flex w-full min-w-0 items-center

            overflow-hidden rounded-xl border
            border-stroke

            transition-all duration-200

            focus-within:border-primary
            focus-within:ring-2
            focus-within:ring-primary/10

            dark:border-strokedark
            dark:bg-meta-4

            ${disabled ? 'cursor-not-allowed opacity-70' : ''}
            ${rightButton || onReload ? 'rounded-r-none' : ''}
          `}
            onClick={() => {
              if (!disabled) setIsOpen((prev) => !prev);
            }}
          >
            {iconKey && (
              <div
                className="
                hidden h-12.5 w-12 shrink-0 items-center justify-center

                border-r border-stroke
                bg-gray-2

                dark:border-strokedark
                dark:bg-boxdark-2

                sm:flex
              "
              >
                <InputIconByKey iconKey={iconKey} />
              </div>
            )}

            <div
              className={`
              min-w-0 max-w-full flex-1
              px-4 py-3.5

              text-sm
              text-black

              dark:text-white

              ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}
            `}
              title={selectedOption ? selectedOption.name : placeholder}
            >
              <span
                className="
                block max-w-full overflow-hidden whitespace-normal break-words
                md:overflow-hidden md:text-ellipsis md:whitespace-nowrap
              "
              >
                {selectedOption ? selectedOption.name : 'Seleccionar'}
              </span>
            </div>

            <div
              role="button"
              tabIndex={0}
              onClick={(event) => {
                event.stopPropagation();

                if (!disabled) {
                  setIsOpen((prev) => !prev);
                }
              }}
              className={`
              flex shrink-0 items-center justify-center pr-4

              transition-transform duration-200

              ${
                disabled
                  ? 'pointer-events-none opacity-40'
                  : 'cursor-pointer hover:opacity-80'
              }
            `}
            >
              <SvgIcon
                name="chevron-down"
                className={`
                h-5 w-5 origin-center transform

                transition-transform duration-300 ease-in-out

                ${
                  isOpen
                    ? 'rotate-180 text-primary'
                    : 'rotate-0 text-body dark:text-bodydark2'
                }
              `}
              />
            </div>
          </div>

          {isOpen && (
            <div
              className="
              absolute z-999999 mt-1 w-full overflow-hidden

              rounded-xl border border-stroke
              bg-white
              shadow-default

              dark:border-strokedark
              dark:bg-boxdark
            "
            >
              <input
                ref={searchInputRef}
                type="text"
                name={name}
                placeholder={searchPlaceholder}
                disabled={disabled}
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                className={`
                bg-gray-1

                w-full border-b
                border-stroke

                px-4 py-3.5

                text-sm
                text-black

                outline-none

                transition-all

                duration-200 placeholder:text-body

                focus:border-primary
                focus:ring-2
                focus:ring-primary/10

                dark:border-strokedark
                dark:bg-meta-4
                dark:text-white
                dark:placeholder:text-bodydark2

                ${disabled ? 'cursor-not-allowed opacity-70' : ''}
              `}
              />

              <ul
                className="
                max-h-60 overflow-y-auto

                bg-white
                dark:bg-boxdark
              "
              >
                {filteredOptions.length > 0 ? (
                  filteredOptions.map((option) => {
                    const isSelected = option.id === selectedValue;

                    return (
                      <li
                        key={option.id}
                        onClick={() => {
                          onSelect(option);
                          setIsOpen(false);
                          setSearchTerm('');
                        }}
                        aria-selected={isSelected}
                        className={`
                        cursor-pointer border-b border-stroke

                        px-4 py-2.5

                        text-sm

                        transition-colors duration-150

                        last:border-b-0

                        dark:border-strokedark

                        ${
                          isSelected
                            ? 'bg-primary text-white'
                            : 'text-black hover:bg-primary hover:text-white dark:text-white'
                        }
                      `}
                        title={option.name}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <span className="block min-w-0 max-w-full overflow-hidden text-ellipsis whitespace-nowrap">
                            {option.name}
                          </span>
                        </div>
                      </li>
                    );
                  })
                ) : (
                  <li
                    className="
                    px-4 py-3

                    text-sm
                    text-body

                    dark:text-bodydark2
                  "
                  >
                    No se encontraron resultados
                  </li>
                )}
              </ul>
            </div>
          )}

          {error && <p className="mt-2 text-sm text-danger">{error}</p>}
        </div>

        {rightButton && (
          <div className="hidden flex-shrink-0 sm:flex">{rightButton}</div>
        )}

        {onReload && (
          <div className="hidden flex-shrink-0 sm:flex">
            <button
              type="button"
              onClick={onReload}
              className="
              flex h-12.5 w-12 items-center justify-center

              rounded-r-xl
              bg-[#3CA745]
              text-white

              outline-none

              transition hover:bg-green-600
            "
              title="Recargar lista"
            >
              <SvgIcon name="refresh" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SimpleSearchSelect;
